<template>
  <v-dialog
    v-model="showDialog"
    content-class="training-yearly-plan-dialog-overlay"
    fullscreen
    transition="dialog-bottom-transition"
  >
    <v-card class="training-yearly-plan-dialog">
      <v-toolbar flat color="white" class="training-yearly-plan-toolbar">
        <v-btn icon @click="close()">
          <v-icon>close</v-icon>
        </v-btn>

        <v-toolbar-title style="font-weight: 600;">
          {{ dialogTitle }}
        </v-toolbar-title>

        <v-spacer />

        <div class="training-yearly-plan-toolbar-actions">
          <v-btn
            v-if="zoomMonth !== null"
            text
            small
            color="primary"
            @click="zoomMonth = null"
          >
            {{ $t("wkt.admin.training-action.yearly-plan.back-to-year") }}
          </v-btn>

          <div class="training-yearly-plan-year-switcher">
            <v-btn icon small @click="changeYear(-1)">
              <v-icon small>chevron_left</v-icon>
            </v-btn>
            <span>{{ year }}</span>
            <v-btn icon small @click="changeYear(1)">
              <v-icon small>chevron_right</v-icon>
            </v-btn>
          </div>

          <v-btn icon class="training-yearly-plan-toolbar-icon" @click="onPrint()">
            <v-icon>print</v-icon>
          </v-btn>
        </div>
      </v-toolbar>

      <v-divider />

      <v-card-text ref="content" class="training-yearly-plan-content" @wheel="onWheel">
        <div ref="printBody" class="training-yearly-plan-body">
          <div class="training-yearly-plan-print-header">
            <div style="font-size: 20px; font-weight: 700;">
              {{ $t("wkt.admin.training-action.yearly-plan.print.title").replace("{year}", `${year}`) }}
            </div>
            <div v-if="company && company.name" style="padding-top: 4px;">
              {{ company.name }}
            </div>
            <div v-if="printGeneratedAt" style="padding-top: 4px; color: #546E7A;">
              {{ printGeneratedAt }}
            </div>
          </div>

          <v-alert
            v-if="!loading && data.itemsWithoutDatesCount > 0"
            dense
            outlined
            color="warning"
            class="mb-0"
          >
            <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;">
              <span>
                {{ $t("wkt.admin.training-action.yearly-plan.without-dates-banner").replace("{count}", `${data.itemsWithoutDatesCount}`) }}
              </span>
              <v-btn text small color="warning" @click="openMissingDatesListing()">
                {{ $t("wkt.admin.training-action.yearly-plan.without-dates-link") }}
              </v-btn>
            </div>
          </v-alert>

          <div v-if="loading" style="padding-top: 12px;">
            <div class="training-yearly-plan-loading">
              <div class="training-yearly-plan-loading-filters">
                <v-skeleton-loader type="text" width="220" />
                <v-skeleton-loader type="text" width="220" />
                <v-skeleton-loader type="text" width="220" />
                <v-skeleton-loader type="text" width="180" />
              </div>

              <div class="training-yearly-plan-loading-card">
                <v-skeleton-loader type="table-thead, table-tbody" />
              </div>
            </div>
          </div>

          <v-alert v-else-if="error" dense text type="error">
            {{ error }}
          </v-alert>

          <div v-else-if="!data.items.length" class="training-yearly-plan-empty">
            <v-icon large color="#90A4AE">timeline</v-icon>
            <div style="padding-top: 12px; font-size: 18px; font-weight: 600;">
              {{ $t("wkt.admin.training-action.yearly-plan.empty").replace("{year}", `${year}`) }}
            </div>
          </div>

          <template v-else>
            <TrainingYearlyPlanFilters
              :categories="data.categories"
              :status-options="statusOptions"
              :mode-options="modeOptions"
              :filters="filters"
              @change="filters = $event"
            />

            <div class="training-yearly-plan-panel">
              <div
                v-if="zoomMonth !== null"
                class="training-yearly-plan-panel-chip"
              >
                {{ formattedZoomMonth }}
              </div>

              <TrainingYearlyTimeline
                :year="year"
                :categories="data.categories"
                :items="data.items"
                :filters="filters"
                :zoom-month="zoomMonth"
                :today="new Date()"
                :status-options="statusOptions"
                :mode-options="modeOptions"
                @item-click="openTrainingAction($event)"
                @month-click="zoomToMonth($event)"
                @category-click="toggleCategoryFilter($event)"
              />
            </div>
          </template>
        </div>
      </v-card-text>

      <TrainingActionDialog
        ref="trainingActionDialog"
        @show-alert="$emit('show-alert', $event)"
        @refresh="fetchYearlyPlan()"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import api from "../../../api/worktime";
import gwt from "../../../api/gwt";
import BaseModule from "../../common/BaseModule.js";
import TrainingActionDialog from "./TrainingActionDialog.vue";
import TrainingYearlyPlanFilters from "../trainingyearlyplan/TrainingYearlyPlanFilters.vue";
import TrainingYearlyTimeline from "../trainingyearlyplan/TrainingYearlyTimeline.vue";

const MODULE_ID = 213;
const PRINT_CSS_VERSION = "3.4";

function createDefaultFilters() {
  return {
    categoryIds: [],
    statuses: [],
    modes: [],
    showCanceled: false,
  };
}

function getCurrentYear() {
  return new Date().getFullYear();
}

export default {
  name: "TrainingYearlyPlanDialog",
  components: {
    TrainingActionDialog,
    TrainingYearlyPlanFilters,
    TrainingYearlyTimeline,
  },
  mixins: [BaseModule],
  data() {
    return {
      showDialog: false,
      loading: false,
      error: null,
      year: getCurrentYear(),
      data: {
        year: getCurrentYear(),
        categories: [],
        items: [],
        itemsWithoutDatesCount: 0,
      },
      filters: createDefaultFilters(),
      zoomMonth: null,
      profile: null,
      company: null,
      printGeneratedAt: "",
    };
  },
  computed: {
    userHasAnyAccess() {
      return this.profile && this.profile.moduleTraining;
    },
    statusOptions() {
      return [
        { id: 0, label: this.$t("wkt.admin.training-action.status.planned") },
        { id: 1, label: this.$t("wkt.admin.training-action.status.open") },
        { id: 2, label: this.$t("wkt.admin.training-action.status.closed") },
        { id: 3, label: this.$t("wkt.admin.training-action.status.in-progress") },
        { id: 4, label: this.$t("wkt.admin.training-action.status.finished") },
        { id: 5, label: this.$t("wkt.admin.training-action.status.canceled") },
      ];
    },
    modeOptions() {
      return [
        { id: 0, label: this.$t("wkt.admin.training-action.mode.presential") },
        { id: 1, label: this.$t("wkt.admin.training-action.mode.online") },
        { id: 2, label: this.$t("wkt.admin.training-action.mode.mixed") },
      ];
    },
    dialogTitle() {
      return `${this.$t("wkt.admin.training-action.yearly-plan.title")} ${this.year}`;
    },
    formattedZoomMonth() {
      if (this.zoomMonth === null || this.zoomMonth === undefined) {
        return "";
      }

      return new Date(this.year, this.zoomMonth, 1).toLocaleDateString(
        [this.$i18n.locale, "en"],
        { month: "long" },
      );
    },
  },
  methods: {
    onWheel(event) {
      if (!event) {
        return;
      }

      event.stopPropagation();
    },
    resetScrollPosition() {
      const content = this.$refs.content;
      if (!content || typeof content.scrollTop !== "number") {
        return;
      }

      content.scrollTop = 0;
      window.requestAnimationFrame(() => {
        content.scrollTop = 0;
      });
      window.setTimeout(() => {
        content.scrollTop = 0;
      }, 0);
    },
    buildPrintStylesheetUrls() {
      const baseUrl = `${window.location.origin}${window.location.pathname || "/"}`;

      return [
        "https://fonts.googleapis.com/css?family=Material+Icons",
        "https://fonts.cdnfonts.com/css/google-sans",
        "https://cdn.jsdelivr.net/npm/vuetify@2.x/dist/vuetify.min.css",
        new URL(`css/worktime-vue.css?v=${PRINT_CSS_VERSION}`, baseUrl).href,
      ];
    },
    buildPrintMarkup() {
      const body = this.$refs.printBody;
      if (!body) {
        return "";
      }

      const clone = body.cloneNode(true);
      const filters = clone.querySelector(".training-yearly-plan-filter-bar");
      const loading = clone.querySelector(".training-yearly-plan-loading");
      const scrollSurface = clone.querySelector(".training-yearly-plan-scroll");

      if (filters && filters.parentNode) {
        filters.parentNode.removeChild(filters);
      }

      if (loading && loading.parentNode) {
        loading.parentNode.removeChild(loading);
      }

      if (scrollSurface) {
        scrollSurface.scrollLeft = 0;
        scrollSurface.scrollTop = 0;
      }

      return clone.outerHTML;
    },
    printInHiddenFrame() {
      const markup = this.buildPrintMarkup();
      if (!markup) {
        window.print();
        return;
      }

      const stylesheetLinks = this.buildPrintStylesheetUrls()
        .map((href) => `<link rel="stylesheet" href="${href}">`)
        .join("");
      const frame = document.createElement("iframe");
      const cleanup = () => {
        window.setTimeout(() => {
          if (frame.parentNode) {
            frame.parentNode.removeChild(frame);
          }
        }, 0);
      };
      const triggerPrint = () => {
        const printWindow = frame.contentWindow;
        if (!printWindow) {
          cleanup();
          window.print();
          return;
        }

        printWindow.focus();
        window.setTimeout(() => {
          printWindow.print();
        }, 100);
      };

      frame.setAttribute("aria-hidden", "true");
      frame.style.position = "fixed";
      frame.style.right = "0";
      frame.style.bottom = "0";
      frame.style.width = "0";
      frame.style.height = "0";
      frame.style.border = "0";
      frame.style.opacity = "0";
      frame.style.pointerEvents = "none";

      document.body.appendChild(frame);

      const doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
      if (!doc) {
        cleanup();
        window.print();
        return;
      }

      doc.open();
      doc.write(`<!DOCTYPE html>
<html lang="${this.$i18n.locale}">
  <head>
    <meta charset="utf-8">
    <title>${this.dialogTitle}</title>
    ${stylesheetLinks}
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        font-family: 'Google Sans', Roboto, 'Helvetica Neue', Arial, sans-serif;
      }
    </style>
  </head>
  <body class="training-yearly-plan-print-window">
    <div class="training-yearly-plan-dialog">
      <div class="training-yearly-plan-content">
        ${markup}
      </div>
    </div>
  </body>
</html>`);
      doc.close();

      if (frame.contentWindow) {
        frame.contentWindow.onafterprint = cleanup;
      }

      window.setTimeout(triggerPrint, 500);
    },
    // eslint-disable-next-line no-unused-vars
    open(action, payload, eventConfig) {
      this.profile = gwt.getSessionProfile();
      this.company = gwt.getSessionInformation() ? gwt.getSessionInformation().company : null;

      if (!this.userHasAnyAccess) {
        this.$emit("show-alert", {
          text: this.$t("wkt.admin.common.error.try_again_later"),
          color: "error",
          timeout: 3000,
        });
        return;
      }

      const payloadYear = payload !== null && payload !== undefined && payload !== ""
        ? parseInt(payload, 10)
        : null;

      this.year = Number.isInteger(payloadYear) ? payloadYear : getCurrentYear();
      this.data = {
        year: this.year,
        categories: [],
        items: [],
        itemsWithoutDatesCount: 0,
      };
      this.filters = createDefaultFilters();
      this.zoomMonth = null;
      this.error = null;
      this.printGeneratedAt = "";
      this.showDialog = true;

      this.$nextTick(() => {
        this.resetScrollPosition();
      });
      this.fetchYearlyPlan();
    },
    close() {
      this.showDialog = false;
      this.zoomMonth = null;
      this.error = null;
    },
    fetchYearlyPlan() {
      this.loading = true;
      this.error = null;

      api.getTrainingYearlyPlan(
        this.year,
        (response) => {
          this.data = response || {
            year: this.year,
            categories: [],
            items: [],
            itemsWithoutDatesCount: 0,
          };
          this.loading = false;
          this.$nextTick(() => {
            this.resetScrollPosition();
          });
        },
        (error) => {
          this.loading = false;
          this.error = error && error.message ? error.message : `${error}`;
        },
      );
    },
    changeYear(offset) {
      this.year += offset;
      this.zoomMonth = null;
      this.fetchYearlyPlan();
    },
    onPrint() {
      this.printGeneratedAt = new Date().toLocaleString([this.$i18n.locale, "en"]);
      this.$nextTick(() => {
        this.printInHiddenFrame();
      });
    },
    openTrainingAction(payload) {
      if (!payload || !payload.trainingActionId || !this.$refs.trainingActionDialog) {
        return;
      }

      this.$refs.trainingActionDialog.open("EDIT", payload.trainingActionId);
    },
    zoomToMonth(payload) {
      if (!payload || payload.month === null || payload.month === undefined) {
        return;
      }

      this.zoomMonth = payload.month;
    },
    toggleCategoryFilter(payload) {
      if (!payload || payload.categoryId === null || payload.categoryId === undefined) {
        return;
      }

      const categoryId = payload.categoryId;
      const nextFilters = {
        ...this.filters,
        categoryIds: [...this.filters.categoryIds],
      };
      const existingIndex = nextFilters.categoryIds.indexOf(categoryId);
      const shiftPressed = payload.originalEvent && payload.originalEvent.shiftKey;

      if (shiftPressed) {
        if (existingIndex === -1) {
          nextFilters.categoryIds.push(categoryId);
        } else {
          nextFilters.categoryIds.splice(existingIndex, 1);
        }
      } else if (existingIndex === -1 || nextFilters.categoryIds.length > 1) {
        nextFilters.categoryIds = [categoryId];
      } else {
        nextFilters.categoryIds = [];
      }

      this.filters = nextFilters;
    },
    openMissingDatesListing() {
      const filterKey = `em-module-filters-${MODULE_ID}`;
      const filters = [
        {
          fields: ["missing_realization_dates"],
          type: "boolean",
          value: "1",
        },
      ];

      try {
        window.localStorage.setItem(filterKey, JSON.stringify(filters));
      } catch (error) {
        console.warn("Unable to persist training yearly plan redirect filters", error);
      }

      this.close();

      window.setTimeout(() => {
        if (window.location.hash !== `#em;id=${MODULE_ID}`) {
          window.location.href = `#em;id=${MODULE_ID}`;
          return;
        }

        window.location.reload();
      }, 50);
    },
  },
};
</script>
